// Radha — real payment gateway (Razorpay) reads + writes, direct path.
//
// Order creation goes through the create-payment-order Edge Function (needs
// the RAZORPAY_KEY_SECRET, which the browser must never hold). Settlement is
// never written by the client — the razorpay-webhook function is the only
// writer of a payment's captured/failed status (migration 047). This file's
// job is: ask for an order, open Checkout, then poll the resulting payment
// row until the webhook has settled it (or a timeout gives up gracefully).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { openRazorpayCheckout, type RazorpayOrder } from "../lib/razorpayCheckout";
import { edgeFunctionErrorMessage } from "../lib/edgeFunctionError";

export interface PaymentRow {
  id: string; invoice_id: string; provider: string;
  provider_order_id: string; provider_payment_id: string | null;
  amount: string; currency: string; status: "created" | "authorized" | "captured" | "failed" | "refunded";
  method: string | null; failure_reason: string | null;
  created_at: string; captured_at: string | null;
}

export function useInvoicePayments(invoiceId: string | null) {
  return useQuery({
    queryKey: ["payments", invoiceId],
    queryFn: async (): Promise<PaymentRow[]> => {
      const { data, error } = await supabase
        .from("payments")
        .select("id, invoice_id, provider, provider_order_id, provider_payment_id, amount, currency, status, method, failure_reason, created_at, captured_at")
        .eq("invoice_id", invoiceId as string)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!invoiceId,
    staleTime: 10_000,
  });
}

export interface Installment {
  id: string; invoice_id: string; seq: number; amount: string; due_date: string;
  status: "pending" | "paid" | "overdue" | "cancelled"; paid_payment_id: string | null;
}

export function useInvoiceInstallments(invoiceId: string | null) {
  return useQuery({
    queryKey: ["payment-installments", invoiceId],
    queryFn: async (): Promise<Installment[]> => {
      const { data, error } = await supabase
        .from("payment_installments")
        .select("id, invoice_id, seq, amount, due_date, status, paid_payment_id")
        .eq("invoice_id", invoiceId as string)
        .order("seq", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!invoiceId,
    staleTime: 10_000,
  });
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Poll a payment row until the webhook settles it captured/failed, or give
 * up after ~30s (the webhook is near-instant in practice; this is a safety
 * margin, not the source of truth — the row itself is). */
async function pollPaymentSettled(paymentId: string): Promise<PaymentRow> {
  for (let i = 0; i < 15; i++) {
    const { data, error } = await supabase
      .from("payments")
      .select("id, invoice_id, provider, provider_order_id, provider_payment_id, amount, currency, status, method, failure_reason, created_at, captured_at")
      .eq("id", paymentId)
      .single();
    if (error) throw error;
    if (data.status === "captured" || data.status === "failed") return data;
    await sleep(2_000);
  }
  throw new Error("STILL_PROCESSING: payment is still being confirmed — check back in a moment");
}

export type PayTarget = { invoiceId: string; installmentId?: string; amount?: number };

/** Full pay flow: create the order, open Checkout, wait for the webhook to
 * settle it. Throws with a readable message on failure/cancel/timeout. */
export function usePayInvoice(tenantName: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ invoiceId, installmentId, amount }: PayTarget): Promise<PaymentRow> => {
      const { data, error } = await supabase.functions.invoke("create-payment-order", {
        body: { invoice_id: invoiceId, installment_id: installmentId, amount },
      });
      if (error) throw new Error(await edgeFunctionErrorMessage(error));
      if (data?.error) throw new Error(data.error);
      const order = data as RazorpayOrder & { payment_id: string };

      await openRazorpayCheckout(order, tenantName); // resolves once submitted client-side
      return pollPaymentSettled(order.payment_id);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["invoicing-direct"] });
      qc.invalidateQueries({ queryKey: ["payments", vars.invoiceId] });
      qc.invalidateQueries({ queryKey: ["payment-installments", vars.invoiceId] });
    },
  });
}

export function useCreateInstallmentPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ invoiceId, installments }: { invoiceId: string; installments: { amount: number; due_date: string }[] }) => {
      const rows = installments.map((inst, i) => ({
        invoice_id: invoiceId, seq: i + 1, amount: inst.amount, due_date: inst.due_date,
      }));
      const { error } = await supabase.from("payment_installments").insert(rows);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["payment-installments", vars.invoiceId] });
    },
  });
}

export function useDeleteInstallment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string; invoiceId: string }) => {
      const { error } = await supabase.from("payment_installments").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["payment-installments", vars.invoiceId] });
    },
  });
}

export function payErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (msg === "DISMISSED") return "Payment window closed";
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
